import { formatDateToString } from '~/helpers'
import i18n from '~/i18n'

export const INSERTION_ORDER_QUERY_MAX_LENGTH = 100

/** Statuses hidden from the picker by default (design eligibility). */
export const LINK_INSERTION_ORDER_DEFAULT_EXCLUDED_STATUSES: InsertionOrderStatus[] =
  ['canceled', 'completed']

const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'UGX',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF',
  // COP: not ISO 4217 zero-decimal, but PAC formats COP without cents
  // (see useAppInfo lengthCentsCurrency / helpers/format-number).
  'COP'
])

const getLocale = (): string => {
  const language = i18n.language || 'pt-BR'

  try {
    Intl.NumberFormat(language)
    return language
  } catch {
    return 'pt-BR'
  }
}

/** Formats `total_value` (smallest currency unit) with the IO's ISO currency. */
export function formatInsertionOrderValue({
  totalValue,
  currency = 'BRL'
}: {
  totalValue?: number
  currency?: string
}): string {
  if (totalValue == null || Number.isNaN(totalValue)) return '-'

  const locale = getLocale()
  const code = currency
  const fractionDigits = ZERO_DECIMAL_CURRENCIES.has(code) ? 0 : 2
  const major = fractionDigits === 0 ? totalValue : totalValue / 100

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: code,
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits
  }).format(major)
}

export const formatInsertionOrderDate = (iso?: string): string => {
  if (!iso) return '-'
  return formatDateToString(iso, 'DD/MM/YYYY')
}

/** Only allow http(s) download URLs from the BFF before window.open. */
export const isSafeHttpUrl = (url?: string): boolean => {
  if (!url) return false

  try {
    const parsed = new URL(url)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

export const clampInsertionOrderQuery = (query: string): string =>
  query.slice(0, INSERTION_ORDER_QUERY_MAX_LENGTH)

export const filterExcludedInsertionOrders = (
  items: InsertionOrder[],
  excludedStatuses?: InsertionOrderStatus[],
  excludedIds?: InsertionOrderId[]
): InsertionOrder[] => {
  if (!excludedStatuses?.length && !excludedIds?.length) return items

  const statuses = new Set(excludedStatuses)
  const ids = new Set(excludedIds)
  return items.filter(item => !statuses.has(item.status) && !ids.has(item.id))
}

export const toLinkInsertionOrderOption = (
  item: InsertionOrder
): LinkInsertionOrderOption => ({
  value: item.id,
  label: item.name,
  data: item
})

/** Yellow “Documento pendente” badge only when document_status is explicitly pending. */
export const isDocumentStatusPending = (
  documentStatus?: InsertionOrderDocumentStatus | null
): boolean => documentStatus === 'pending'
